import BaseListPresenter from "../../base/BaseListPresenter";

class RecruiterCalendarPresenter extends BaseListPresenter {
  constructor(
    view,
    findObjectUseCase,
    countObjectUseCase,
    deleteObjectUseCase,
    upsertUseCase,
    exportCSVUseCase,
    addSchemaUseCase,
    updateSchemaUseCase,
    deleteSchemaUseCase
  ) {
    super(view, findObjectUseCase, countObjectUseCase, deleteObjectUseCase);
    this.upsertUseCase = upsertUseCase;
    this.exportCSVUseCase = exportCSVUseCase;
    this.addSchemaUseCase = addSchemaUseCase;
    this.updateSchemaUseCase = updateSchemaUseCase;
    this.deleteSchemaUseCase = deleteSchemaUseCase;
  }

  componentDidMount() {
    this.init();
    this.getLocations();
    return this.getObjects();
  }

  init() {
    const user = this.view.getCurrentUser();
    this.limit = 50;
    this.where = {};
    this.search = {};
    this.filter = {};
    this.include = ["applicant", "employee"];
    this.keys = undefined;
    this.sort = { startDate: 1, startTime: 1 };
    this.progress = true;
    this.reset();
  }

  createQuery() {
    const skip = (this.current - 1) * this.limit;
    const query = {
      limit: this.limit,
      skip: skip,
      where: { ...this.where, ...this.search, ...this.filter },
      include: this.include,
    };
    if (this.sort) {
      query.sort = this.sort;
    }
    const keys = this.keys || this.view.getKeys() || [];
    if (keys.length > 0) {
      query.keys = keys;
    }
    return query;
  }

  async getRecruiterEvents(recruiter) {
    const collection = "recruiter_calendar";
    const recruiterId = recruiter.id;

    if (!recruiterId) {
      console.error("No recruiter ID provided to getRecruiterEvents");
      return [];
    }

    try {
      console.log("Fetching events from collection:", collection);
      console.log("Using specific recruiter ID for filtering:", recruiterId);

      const query = {
        where: {
          "recruiter.id": recruiterId,
        },
        include: ["applicant", "employee"],
        sort: { startDate: 1, startTime: 1 },
        timestamp: Date.now(),
      };

      console.log("Query for events:", JSON.stringify(query));
      const events = await this.findObjectUseCase.execute(collection, query);
      console.log("Events retrieved:", events?.length || 0);

      if (!events) {
        console.warn("No events found or undefined response");
        return [];
      }

      if (!Array.isArray(events)) {
        console.warn("Events is not an array, converting to array format");
        return typeof events === "object" ? [events] : [];
      }

      const eventsWithApplicants = await this.fetchApplicantDetails(events);

      return eventsWithApplicants.map((event) => {
        if (event.startTime && !event.startTime.includes(":")) {
          const hour = parseInt(event.startTime, 10);
          event.startTime = `${hour.toString().padStart(2, "0")}:00`;
        }

        if (event.endTime && !event.endTime.includes(":")) {
          const hour = parseInt(event.endTime, 10);
          event.endTime = `${hour.toString().padStart(2, "0")}:00`;
        }

        return event;
      });
    } catch (error) {
      console.error("Error fetching recruiter events:", error);
      return [];
    }
  }

  async fetchApplicantDetails(events) {
    const applicantIdsToFetch = events
      .filter(
        (event) =>
          (event.applicant && typeof event.applicant === "string") ||
          (typeof event.applicant === "object" &&
            event.applicant.id &&
            !event.applicant.name &&
            !event.applicant.fullName)
      )
      .map((event) =>
        typeof event.applicant === "string"
          ? event.applicant
          : event.applicant.id
      );

    const employeeIdsToFetch = events
      .filter(
        (event) =>
          (event.employee && typeof event.employee === "string") ||
          (typeof event.employee === "object" &&
            event.employee.id &&
            !event.employee.name &&
            !event.employee.fullName)
      )
      .map((event) =>
        typeof event.employee === "string" ? event.employee : event.employee.id
      );

    const uniqueApplicantIds = [...new Set(applicantIdsToFetch)];
    const uniqueEmployeeIds = [...new Set(employeeIdsToFetch)];

    try {
      if (uniqueApplicantIds.length > 0) {
        console.log("Fetching applicant details for IDs:", uniqueApplicantIds);
        const applicantQuery = {
          where: {
            id: { $in: uniqueApplicantIds },
          },
        };
        const applicants = await this.findObjectUseCase.execute(
          "applicants",
          applicantQuery
        );

        if (applicants && applicants.length > 0) {
          const applicantMap = {};
          applicants.forEach((applicant) => {
            applicantMap[applicant.id] = applicant;
          });

          events = events.map((event) => {
            if (event.applicant) {
              const applicantId =
                typeof event.applicant === "string"
                  ? event.applicant
                  : event.applicant.id;

              if (applicantMap[applicantId]) {
                event.applicant = applicantMap[applicantId];
              }
            }
            return event;
          });
        }
      }

      if (uniqueEmployeeIds.length > 0) {
        console.log("Fetching employee details for IDs:", uniqueEmployeeIds);
        const employeeQuery = {
          where: {
            id: { $in: uniqueEmployeeIds },
          },
        };
        const employees = await this.findObjectUseCase.execute(
          "employees",
          employeeQuery
        );

        if (employees && employees.length > 0) {
          const employeeMap = {};
          employees.forEach((employee) => {
            employeeMap[employee.id] = employee;
          });

          events = events.map((event) => {
            if (event.employee) {
              const employeeId =
                typeof event.employee === "string"
                  ? event.employee
                  : event.employee.id;

              if (employeeMap[employeeId]) {
                event.employee = employeeMap[employeeId];
              }
            }
            return event;
          });
        }
      }
    } catch (error) {
      console.error("Error fetching associated details:", error);
    }

    return events;
  }

  async findObjects() {
    const collection = this.view.getCollectionName();
    const query = this.createQuery();
    const currentUser = this.view.getCurrentUser();

    if (currentUser && currentUser.id) {
      query.where = {
        ...query.where,
        "recruiter.id": currentUser.id,
      };
    }

    try {
      this.showProgress();
      this.findObjectUseCase.abort();
      const objects = await this.findObjectUseCase.execute(collection, query);
      this.objects = this.objects.concat(objects);
      this.view.setTotal(this.objects.length);
      this.view.setObjects(this.objects);
      this.hideProgress();
    } catch (error) {
      this.hideProgress();
      this.view.showError(error);
    }
    this.progress = false;
  }

  async getLocations() {
    const collection = "locations";
    try {
      if (this.findObjectUseCase?.abort) {
        this.findObjectUseCase.abort();
      }

      const objects = await this.findObjectUseCase.execute(collection);
      this.view.setState({ locations: objects });
    } catch (error) {
      console.error("Error fetching locations:", error);
      this.view.showError(error);
    } finally {
      this.progress = false;
    }
  }

  onClickAdd() {
    this.view.navigateTo("/recruiter/calendar/form");
  }

  async onClickView(calendarDetails) {
    this.view.navigateTo(`/recruiter/calendar/detail/${calendarDetails.id}`);
  }

  onChangeLocation(value, schema) {
    this.reset();
    this.filter = {
      location: {
        name: {
          $regex: value,
          $options: "i",
        },
      },
    };
    this.getObjects();
  }

  onChangeReference(value, schema) {
    this.reset();
    this.filter = {
      referenceNumber: { $regex: value, $options: "i" },
    };
    this.getObjects();
  }
}

export default RecruiterCalendarPresenter;
